import React, { useState, useEffect } from 'react';
import './OpeningHours.css';
import { TimeInput } from '../Common/TimeInput';
import DateRangeListEditor from './DateRangeListEditor';
import { validateDateRanges } from '../../utils/dateRanges';

const DAYS_DE = {
  monday: 'Montag',
  tuesday: 'Dienstag',
  wednesday: 'Mittwoch',
  thursday: 'Donnerstag',
  friday: 'Freitag',
  saturday: 'Samstag',
  sunday: 'Sonntag'
};

const DAYS_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const OpeningHoursEditor = ({
  openingHours,
  onSave,
  onImport,
  canImport = true,
  dateRanges = [],
  isSeasonal = false,
  onValidityChange,
  isEditMode = false
}) => {
  const [hours, setHours] = useState(openingHours || []);
  const [localRanges, setLocalRanges] = useState(dateRanges || []);
  const [localIsSeasonal, setLocalIsSeasonal] = useState(isSeasonal);
  const [importing, setImporting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [validated, setValidated] = useState(false);

  // Update local state when props change
  useEffect(() => {
    setHours(openingHours || []);
    
    // Mark as validated if opening hours are loaded with data, or if in edit mode
    if (isEditMode || (openingHours && openingHours.length > 0)) {
      setValidated(true);
    } else {
      setValidated(false);
    }
  }, [openingHours, isEditMode]);

  useEffect(() => {
    setLocalRanges(dateRanges || []);
  }, [dateRanges]);

  useEffect(() => {
    setLocalIsSeasonal(isSeasonal);
  }, [isSeasonal]);

  const handleImport = async () => {
    setImporting(true);
    setMessage(null);
    try {
      const result = await onImport();
      if (result && result.opening_hours && result.opening_hours.length > 0) {
        setHours(result.opening_hours);
        setMessage({ type: 'success', text: 'Öffnungszeiten erfolgreich importiert!' });
      } else if (result && result.opening_hours_text && result.opening_hours_text.length > 0) {
        // Google hat Textdaten aber keine strukturierten Daten
        setMessage({ 
          type: 'warning', 
          text: 'Öffnungszeiten gefunden, aber nicht im strukturierten Format. Bitte manuell eingeben.' 
        });
      } else {
        setMessage({ type: 'error', text: 'Keine Öffnungszeiten gefunden. Bitte manuell eingeben.' });
      }
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.error || error.message || 'Fehler beim Importieren der Öffnungszeiten' 
      });
    } finally {
      setImporting(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    
    // Validierung: Öffnungszeiten müssen kleiner als Schließzeiten sein
    for (const dayHour of hours) {
      if (dayHour.opens && dayHour.closes && dayHour.opens >= dayHour.closes) {
        setMessage({ 
          type: 'error', 
          text: `${DAYS_DE[dayHour.day]}: Die Öffnungszeit muss vor der Schließzeit liegen!` 
        });
        setSaving(false);
        return;
      }
    }
    
    // Validierung: Datumsbereiche (inkl. Verkettungsregel bei mehreren Zeiträumen)
    const rangeError = validateDateRanges(localRanges);
    if (rangeError) {
      setMessage({ type: 'error', text: rangeError });
      setSaving(false);
      return;
    }

    try {
      await onSave(hours);

      // Save validity data separately if handler provided
      if (onValidityChange) {
        onValidityChange({
          dateRanges: localRanges.filter((r) => r.from || r.until),
          isSeasonal: localIsSeasonal
        });
      }
      
      setValidated(true);
      setMessage({ type: 'success', text: 'Öffnungszeiten validiert!' });
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.error || 'Fehler beim Validieren' 
      });
    } finally {
      setSaving(false);
    }
  };

  const updateDay = (day, field, value) => {
    setValidated(false); // Reset validation when data changes
    const existing = hours.find(h => h.day === day);
    if (existing) {
      setHours(hours.map(h => 
        h.day === day ? { ...h, [field]: value } : h
      ));
    } else {
      setHours([...hours, { day, opens: '', closes: '', [field]: value }]);
    }
  };

  const removeDay = (day) => {
    setValidated(false); // Reset validation when data changes
    setHours(hours.filter(h => h.day !== day));
  };

  const addDay = (day) => {
    setValidated(false); // Reset validation when data changes
    if (!hours.find(h => h.day === day)) {
      setHours([...hours, { day, opens: '09:00', closes: '17:00' }]);
    }
  };

  const getDayData = (day) => hours.find(h => h.day === day) || null;

  return (
    <div className="opening-hours-editor">
      <div className="opening-hours-header">
        <h3>Öffnungszeiten</h3>
        <div className="opening-hours-actions">
          {canImport && (
            <button
              type="button"
              onClick={handleImport}
              disabled={importing}
              className="btn btn-secondary"
            >
              {importing ? 'Importiere...' : '📍 Von Google importieren'}
            </button>
          )}
        </div>
      </div>

      {message && (
        <div className={`message message-${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="opening-hours-list">
        {DAYS_ORDER.map(day => {
          const dayData = getDayData(day);
          
          return (
            <div key={day} className="opening-hours-day">
              <div className="day-header">
                <label>
                  <input
                    type="checkbox"
                    checked={!!dayData}
                    onChange={(e) => e.target.checked ? addDay(day) : removeDay(day)}
                  />
                  <span className="day-name">{DAYS_DE[day]}</span>
                </label>
              </div>
              
              {dayData && (
                <div className="day-times">
                  <TimeInput
                    value={dayData.opens || ''}
                    onChange={(e) => updateDay(day, 'opens', e.target.value)}
                    className="time-input"
                  />
                  <span>bis</span>
                  <TimeInput
                    value={dayData.closes || ''}
                    onChange={(e) => updateDay(day, 'closes', e.target.value)}
                    className="time-input"
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Validity and Seasonal Section */}
      <div className="opening-hours-meta">
        <div className="seasonal-toggle">
          <label className="seasonal-label">
            <input
              type="checkbox"
              checked={localIsSeasonal}
              onChange={(e) => setLocalIsSeasonal(e.target.checked)}
              className="seasonal-checkbox"
            />
            <span className="seasonal-text">Saisonale Aktivität</span>
          </label>
        </div>

        <div className="validity-dates-ranges">
          <label className="validity-label">Gültig in folgenden Zeiträumen (optional)</label>
          <DateRangeListEditor
            ranges={localRanges}
            onChange={(ranges) => {
              setLocalRanges(ranges);
              setValidated(false); // Reset validation when data changes
            }}
            labelFrom="Gültig ab"
            labelUntil="Gültig bis"
          />
        </div>
      </div>

      {/* Validate Button at Bottom */}
      <div className="opening-hours-save-section mt-4">
        <button
          type="button"
          onClick={handleSave}
          disabled={
            saving || 
            validated || 
            hours.length === 0 || 
            hours.some(h => !h.opens || !h.closes)
          }
          className="btn btn-primary"
        >
          {saving ? 'Validiere...' : validated ? 'Validiert' : 'Validieren'}
        </button>
        {!validated && (
          <p className="validate-hint">Validieren für die Erstellung der Aktivität erforderlich.</p>
        )}
      </div>
    </div>
  );
};

export default OpeningHoursEditor;
